/**
 * Intenta obtener un código ISO 4217 desde valores sueltos u objetos típicos del backend.
 */
export function tryParseCurrencyValue(raw: unknown): string | null {
  if (raw == null || raw === '') return null;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed ? trimmed.toUpperCase() : null;
  }

  if (typeof raw === 'object' && raw !== null) {
    const o = raw as Record<string, unknown>;
    const valueNested =
      typeof o.value === 'string' ? o.value : (o.value as Record<string, unknown> | null | undefined)?.value;
    const currencyNested =
      typeof o.currency === 'object' && o.currency !== null ? (o.currency as Record<string, unknown>) : undefined;
    const candidates: unknown[] = [
      o.code,
      o.iso_code,
      o.currency_code,
      o.iso,
      o.iso_4217_code,
      o.iso4217_code,
      o.currency_iso,
      o.abbreviation,
      valueNested,
      currencyNested?.code,
      currencyNested?.iso_code,
      currencyNested?.currency_iso,
    ];
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) {
        return c.trim().toUpperCase();
      }
    }
  }

  return null;
}

/**
 * Un solo valor de API (string u objeto anidado). Si no es parseable, devuelve `fallback`.
 */
export function resolveCurrencyFromApi(raw: unknown, fallback = 'EUR'): string {
  return tryParseCurrencyValue(raw) ?? fallback;
}

function pushRelationCurrencyFields(acc: unknown[], rel: unknown): void {
  if (!rel || typeof rel !== 'object') return;
  const o = rel as Record<string, unknown>;
  acc.push(o.currency, o.currency_code, o.currency_iso, o.currencyIso, o.default_currency, o.cod_currency);
}

function collectPayoutCurrencyBuckets(row: Record<string, unknown>): unknown[] {
  const buckets: unknown[] = [row.currency, row.currency_code, row.currency_iso, row.currencyIso];
  pushRelationCurrencyFields(buckets, row.client);
  pushRelationCurrencyFields(buckets, row.wallet);
  return buckets;
}

/**
 * Moneda en filas de payout si el API la envía (raíz o relaciones). Si no hay → `undefined`.
 * El listado `/cod-payouts` a veces no incluye `currency`; en UI se usa la moneda del team.
 */
export function tryResolvePayoutRowCurrency(row: Record<string, unknown>): string | undefined {
  for (const v of collectPayoutCurrencyBuckets(row)) {
    const iso = tryParseCurrencyValue(v);
    if (iso) return iso;
  }
  return undefined;
}

function collectPayoutItemCurrencyBuckets(row: Record<string, unknown>): unknown[] {
  const buckets: unknown[] = [row.currency, row.currency_code, row.currency_iso, row.currencyIso];
  pushRelationCurrencyFields(buckets, row.cod_payout);
  const order = row.order;
  if (order && typeof order === 'object') {
    const o = order as Record<string, unknown>;
    pushRelationCurrencyFields(buckets, order);
    pushRelationCurrencyFields(buckets, o.shop);
  }
  return buckets;
}

/** Ítems de payout cuando el API envía moneda en la fila o relaciones; si no, `undefined`. */
export function tryResolveCodPayoutItemRowCurrency(row: Record<string, unknown>): string | undefined {
  for (const v of collectPayoutItemCurrencyBuckets(row)) {
    const iso = tryParseCurrencyValue(v);
    if (iso) return iso;
  }
  return undefined;
}

/**
 * ISO a formatear: la del recurso si existe; si no, moneda del equipo (`useCurrency()`).
 */
export function resolveCodDisplayCurrency(fromEntity: string | null | undefined, teamCurrency: string): string {
  return tryParseCurrencyValue(fromEntity) ?? teamCurrency;
}
